"use client";

import { buildInitialCollected } from "@/lib/actions/resolution/initialCollected";
import { resolveTerminalTargetIds } from "@/lib/actions/resolution/resolveTargetIds";
import type {
  CollectedValues,
  PrefetchedContact,
  StepDescriptor,
} from "@/lib/actions/resolution/types";
import type { ResolverEntityKey } from "@/lib/actions/relationships/types";

import type { ActionLaunchHints } from "@/lib/actions/launch/types";
import {
  isResolverNextApiResponse,
} from "@/lib/actions/resolution/apiTypes";

import type {
  ActionManifest,
  ClientRunHooks,
  ClientRunResolved,
} from "./types";

export type ClientRedirectFastPathResult =
  | { ok: true }
  | { ok: false; reason: "needs_ui" | "error"; message?: string };

/** Fully automatic client-redirect (no pick/fill/confirm UI). */
export function canDirectClientRedirect(
  manifest: ActionManifest,
  sourceEntity: ResolverEntityKey,
): boolean {
  return (
    manifest.mode === "client-redirect" &&
    !!manifest.clientRun &&
    manifest.requires.autoSelectAllTerminal === true &&
    sourceEntity === "company"
  );
}

function buildResolved(
  manifest: ActionManifest,
  collected: CollectedValues,
  prefetchedTargets?: PrefetchedContact[],
): ClientRunResolved {
  const targetIds = resolveTerminalTargetIds(collected);

  return {
    actionId: collected.actionId,
    targetEntity: manifest.requires.entity,
    targetIds,
    fields: collected.fields,
    extras: collected.extras,
    sourceEntity: collected.sourceEntity,
    sourceId: collected.sourceId,
    prefetchedTargets,
  };
}

function stepNeedsUi(step: StepDescriptor): boolean {
  return step.kind === "pick" || step.kind === "fill" || step.kind === "confirm";
}

/**
 * Resolve and run a client-redirect manifest in one round trip — no resolver
 * modal. Falls back to modal flow when the action needs user input.
 */
function tryRunFromLaunchHints(
  manifest: ActionManifest,
  input: {
    actionId: string;
    sourceEntity: ResolverEntityKey;
    sourceId: string;
  },
  hooks: ClientRunHooks,
  launchHints?: ActionLaunchHints,
): Promise<ClientRedirectFastPathResult> | null {
  const prefetchedTargets = launchHints?.prefetchedTargets;
  if (!prefetchedTargets?.length || !manifest.clientRun) {
    return null;
  }

  const collected = buildInitialCollected(input);
  collected.bulkPick = { ids: prefetchedTargets.map((target) => target.id) };

  return Promise.resolve(
    manifest.clientRun(
      buildResolved(manifest, collected, prefetchedTargets),
      hooks,
    ),
  )
    .then(() => ({ ok: true as const }))
    .catch((error: unknown) => ({
      ok: false as const,
      reason: "error" as const,
      message: error instanceof Error ? error.message : "clientRun failed",
    }));
}

export async function runClientRedirectFastPath(
  manifest: ActionManifest,
  input: {
    actionId: string;
    sourceEntity: ResolverEntityKey;
    sourceId: string;
  },
  hooks: ClientRunHooks,
  launchHints?: ActionLaunchHints,
): Promise<ClientRedirectFastPathResult> {
  if (!manifest.clientRun) {
    return { ok: false, reason: "error", message: "Missing clientRun" };
  }

  const contextOnly = await tryRunFromLaunchHints(
    manifest,
    input,
    hooks,
    launchHints,
  );
  if (contextOnly) {
    return contextOnly;
  }

  const res = await fetch("/api/actions/resolver/next", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      collected: buildInitialCollected(input),
    }),
  });

  const payload: unknown = await res.json();

  if (!isResolverNextApiResponse(payload)) {
    const message =
      payload &&
      typeof payload === "object" &&
      "message" in payload &&
      typeof (payload as { message: unknown }).message === "string"
        ? (payload as { message: string }).message
        : "Resolver failed";
    return {
      ok: false,
      reason: "error",
      message,
    };
  }

  if (payload.step.kind === "error") {
    return { ok: false, reason: "error", message: payload.step.message };
  }

  if (stepNeedsUi(payload.step)) {
    return { ok: false, reason: "needs_ui" };
  }

  if (payload.step.kind !== "run") {
    return { ok: false, reason: "needs_ui" };
  }

  await manifest.clientRun(
    buildResolved(manifest, payload.collected, payload.prefetchedTargets),
    hooks,
  );

  return { ok: true };
}
